import { useLayoutEffect } from "react";
import type { RefObject } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { CalendarEventCard, HOUR_HEIGHT } from "./CalendarEventCard";
import type { CalendarEvent } from "./schedule-events";

const DAY_HEIGHT = 24 * HOUR_HEIGHT;
const CENTER = 2 * DAY_HEIGHT;

type Props = {
  days: { date: Date; events: CalendarEvent[] }[];
  scrollRef: RefObject<HTMLDivElement | null>;
};

export function CyclicCalendar({ days, scrollRef }: Props) {
  const { t } = useTranslation();
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = CENTER;
    }
  }, [scrollRef]);

  return (
    <div ref={scrollRef} tabIndex={0} role="region" aria-label={t("visualizer.calendar")}
      className="h-[480px] overflow-y-scroll overscroll-y-contain [overflow-anchor:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden focus-visible:outline-2 focus-visible:outline-neutral-900"
      onScroll={event => {
        const container = event.currentTarget;
        if (container.scrollTop < DAY_HEIGHT || container.scrollTop > 3 * DAY_HEIGHT) {
          const cycles = Math.round((container.scrollTop - CENTER) / DAY_HEIGHT);
          const target = container.scrollTop - cycles * DAY_HEIGHT;
          container.scrollTop = target;
        }
      }}>
      {[0, 1, 2, 3, 4].map(cycle => (
        <div key={cycle} data-cycle={cycle} className="relative grid grid-cols-[48px_repeat(7,minmax(0,1fr))]"
          style={{ height: DAY_HEIGHT, backgroundImage: "linear-gradient(to bottom, #f0f0f0 0% 25%, #ffffff 25% 75%, #f0f0f0 75% 100%)" }}>
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-10 border-t-2 border-neutral-500" />
          <div>{Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} className={`border-t border-neutral-200 pr-1 pt-1 text-right font-mono text-[10px] ${hour === 0 ? "font-bold text-neutral-800" : "text-neutral-500"}`} style={{ height: HOUR_HEIGHT }}>
              {String(hour).padStart(2, "0")}:00
            </div>
          ))}</div>
          {days.map(day => (
            <div key={day.date.toISOString()} data-day={format(day.date, "yyyy-MM-dd")} className="relative border-l border-neutral-200"
              style={{ backgroundImage: "linear-gradient(to bottom, #e5e5e5 1px, transparent 1px)", backgroundSize: `100% ${HOUR_HEIGHT}px` }}>
              {day.events.map(event => <CalendarEventCard key={event.id} event={event} tabIndex={cycle === 2 ? 0 : -1} />)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
